/**
 * Perimetro del configuratore (istanza in gara) — contratto condiviso.
 *
 * Le funzioni restano agnostiche sulle identità (fornitore, insegna, canale,
 * marchio prodotto, listino, liste modelli): le identità sono PARAMETRI della
 * testata gara (fornitore) o del configuratore (questo perimetro), applicati
 * dal motore in ammissione, prima di qualsiasi funzione.
 *
 * Semantica:
 *  - dimensione non dichiarata (assente / ids vuoti) = tutto entra;
 *  - dimensione dichiarata + fatto assente nella busta evento = fuori
 *    (fail-closed, motivo `perimeter_fact_missing`), mai "entra perché non è
 *    escluso";
 *  - le dimensioni sono in AND; ciascuna sceglie il binario a cui si applica
 *    (CONTA, REMUNERA o entrambi — es. "Apple conta nei volumi ma non nel
 *    fatturato scontato").
 *
 * Marchio prodotto = `pos_brands` (≠ insegna `retail_brands`, ≠ fornitore
 * `pos_suppliers`: il fornitore vive in testata, NON qui).
 */
import { z } from 'zod';

/** Binario a cui si applica una dimensione del perimetro. */
export const COMMISSIONING_PERIMETER_APPLIES_TO = ['both', 'count', 'remunerate'] as const;
export type CommissioningPerimeterAppliesTo = typeof COMMISSIONING_PERIMETER_APPLIES_TO[number];

/** Dimensioni del perimetro (ordine = ordine di presentazione in form e scheda). */
export const COMMISSIONING_PERIMETER_DIMENSIONS = [
  'included_brand_ids',
  'excluded_brand_ids',
  'included_list_ids',
  'excluded_list_ids',
  'price_list_ids',
  'category_ids',
  'product_type_ids',
] as const;
export type CommissioningPerimeterDimension = typeof COMMISSIONING_PERIMETER_DIMENSIONS[number];

/** Etichette italiane delle dimensioni (uniche per form, scheda, preflight). */
export const COMMISSIONING_PERIMETER_DIMENSION_LABELS: Record<CommissioningPerimeterDimension, string> = {
  included_brand_ids: 'Marchi prodotto inclusi',
  excluded_brand_ids: 'Marchi prodotto esclusi',
  included_list_ids: 'Liste modelli incluse',
  excluded_list_ids: 'Liste modelli escluse',
  price_list_ids: 'Listini',
  category_ids: 'Categorie',
  product_type_ids: 'Tipologie prodotto',
};

export const COMMISSIONING_PERIMETER_APPLIES_TO_LABELS: Record<CommissioningPerimeterAppliesTo, string> = {
  both: 'CONTA e REMUNERA',
  count: 'Solo CONTA',
  remunerate: 'Solo REMUNERA',
};

/** Sorgente delle opzioni di una dimensione (endpoint opzioni perimetro). */
export const COMMISSIONING_PERIMETER_DIMENSION_SOURCE = {
  included_brand_ids: 'brands',
  excluded_brand_ids: 'brands',
  included_list_ids: 'lists',
  excluded_list_ids: 'lists',
  price_list_ids: 'price_lists',
  category_ids: 'categories',
  product_type_ids: 'product_types',
} as const satisfies Record<CommissioningPerimeterDimension, string>;
export type CommissioningPerimeterOptionSource =
  typeof COMMISSIONING_PERIMETER_DIMENSION_SOURCE[CommissioningPerimeterDimension];

const uuid = z.string().uuid();

/** Una dimensione dichiarata: id (non vuoti, senza duplicati) + binario. */
export const commissioningPerimeterDimensionSchema = z.object({
  ids: z.array(uuid).min(1).max(500)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'Riferimento duplicato nella dimensione' }),
  applies_to: z.enum(COMMISSIONING_PERIMETER_APPLIES_TO),
}).strict();
export type CommissioningPerimeterDimensionValue = z.infer<typeof commissioningPerimeterDimensionSchema>;

function overlap(a: readonly string[] | undefined, b: readonly string[] | undefined): string[] {
  if (!a || !b) return [];
  const right = new Set(b);
  return a.filter((id) => right.has(id));
}

/**
 * Perimetro completo: ogni dimensione è facoltativa; inclusi ed esclusi della
 * stessa dimensione non possono sovrapporsi (errore tipizzato sulla path).
 * Un oggetto senza dimensioni equivale a nessun filtro (vedi
 * `commissioningPerimeterIsEmpty`).
 */
export const commissioningInstancePerimeterSchema = z.object({
  included_brand_ids: commissioningPerimeterDimensionSchema.optional(),
  excluded_brand_ids: commissioningPerimeterDimensionSchema.optional(),
  included_list_ids: commissioningPerimeterDimensionSchema.optional(),
  excluded_list_ids: commissioningPerimeterDimensionSchema.optional(),
  price_list_ids: commissioningPerimeterDimensionSchema.optional(),
  category_ids: commissioningPerimeterDimensionSchema.optional(),
  product_type_ids: commissioningPerimeterDimensionSchema.optional(),
}).strict().superRefine((v, ctx) => {
  if (overlap(v.included_brand_ids?.ids, v.excluded_brand_ids?.ids).length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['excluded_brand_ids'],
      message: 'Un marchio non può essere sia incluso sia escluso',
    });
  }
  if (overlap(v.included_list_ids?.ids, v.excluded_list_ids?.ids).length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['excluded_list_ids'],
      message: 'Una lista modelli non può essere sia inclusa sia esclusa',
    });
  }
});
export type CommissioningInstancePerimeter = z.infer<typeof commissioningInstancePerimeterSchema>;

/** Dimensioni dichiarate (con almeno un id) in ordine canonico. */
export function commissioningPerimeterDeclaredDimensions(
  perimeter: CommissioningInstancePerimeter | null | undefined,
): CommissioningPerimeterDimension[] {
  if (!perimeter) return [];
  return COMMISSIONING_PERIMETER_DIMENSIONS.filter((dim) => (perimeter[dim]?.ids.length ?? 0) > 0);
}

/** true = nessun filtro (null o nessuna dimensione dichiarata). */
export function commissioningPerimeterIsEmpty(
  perimeter: CommissioningInstancePerimeter | null | undefined,
): boolean {
  return commissioningPerimeterDeclaredDimensions(perimeter).length === 0;
}

/**
 * Normalizza il perimetro per la persistenza: dimensioni vuote rimosse,
 * nessuna dimensione → null (identico a "nessun perimetro").
 */
export function commissioningPerimeterNormalize(
  perimeter: CommissioningInstancePerimeter | null | undefined,
): CommissioningInstancePerimeter | null {
  const declared = commissioningPerimeterDeclaredDimensions(perimeter);
  if (!perimeter || declared.length === 0) return null;
  const out: CommissioningInstancePerimeter = {};
  for (const dim of declared) {
    const value = perimeter[dim];
    if (value) out[dim] = { ids: [...value.ids], applies_to: value.applies_to };
  }
  return out;
}

/** Sovrapposizioni inclusi/esclusi (per la UI: stessa logica del refine). */
export function commissioningPerimeterOverlaps(
  perimeter: CommissioningInstancePerimeter | null | undefined,
): { brands: string[]; lists: string[] } {
  return {
    brands: overlap(perimeter?.included_brand_ids?.ids, perimeter?.excluded_brand_ids?.ids),
    lists: overlap(perimeter?.included_list_ids?.ids, perimeter?.excluded_list_ids?.ids),
  };
}

/** Il binario `applies_to` copre il binario richiesto? */
export function commissioningPerimeterAppliesToTrack(
  appliesTo: CommissioningPerimeterAppliesTo, track: 'count' | 'remunerate',
): boolean {
  return appliesTo === 'both' || appliesTo === track;
}

// ---------------------------------------------------------------------------
// Perimetro RISOLTO dalla foto pubblicata (specchio SQL dei fatti di periodo)
// ---------------------------------------------------------------------------

/** Chiavi del payload normalizzato (busta evento) lette dal perimetro. */
export const COMMISSIONING_PERIMETER_FACT_KEYS = [
  'productBrandId', 'productId', 'priceListId', 'categoryId', 'productTypeId',
] as const;
export type CommissioningPerimeterFactKey = typeof COMMISSIONING_PERIMETER_FACT_KEYS[number];

/** Fatto della busta richiesto da ciascuna dimensione (unica mappa per pipeline e SQL). */
export const COMMISSIONING_PERIMETER_DIMENSION_FACT_KEY: Record<CommissioningPerimeterDimension, CommissioningPerimeterFactKey> = {
  included_brand_ids: 'productBrandId',
  excluded_brand_ids: 'productBrandId',
  included_list_ids: 'productId',
  excluded_list_ids: 'productId',
  price_list_ids: 'priceListId',
  category_ids: 'categoryId',
  product_type_ids: 'productTypeId',
};

/**
 * Perimetro risolto per lo storico dei fatti di periodo (Soglie): le liste
 * modelli sono già espanse negli id prodotto dai valori CONGELATI nella foto.
 * Lo storico è il binario CONTA: qui pesano solo le dimensioni `both`/`count`;
 * le dimensioni solo REMUNERA non filtrano i fatti (contano, non pagano).
 * Il fatto assente su QUALSIASI dimensione dichiarata esclude l'evento
 * (`requiredFactKeys`), come `perimeter_fact_missing` in pipeline.
 */
export interface CommissioningResolvedInstancePerimeter {
  /** Chiavi payload richieste da ogni dimensione dichiarata (qualunque binario). */
  requiredFactKeys: readonly CommissioningPerimeterFactKey[];
  /** Vincoli del binario CONTA; `null` = dimensione non dichiarata su CONTA. */
  count: {
    includedBrandIds: readonly string[] | null;
    excludedBrandIds: readonly string[] | null;
    /** Unione dei prodotti delle liste incluse risolte (lista non risolta = nessun prodotto). */
    includedProductIds: readonly string[] | null;
    /** Unione dei prodotti delle liste escluse risolte. */
    excludedProductIds: readonly string[] | null;
    priceListIds: readonly string[] | null;
    categoryIds: readonly string[] | null;
    productTypeIds: readonly string[] | null;
    /** true = una lista ESCLUSA non è risolvibile nella foto: nessun evento attraversa CONTA (fail-closed). */
    admitsNothing: boolean;
  };
}

function countIds(
  perimeter: CommissioningInstancePerimeter, dimension: CommissioningPerimeterDimension,
): readonly string[] | null {
  const value = perimeter[dimension];
  if (!value || value.ids.length === 0 || !commissioningPerimeterAppliesToTrack(value.applies_to, 'count')) return null;
  return value.ids;
}

/**
 * Risolve il perimetro con un resolver di liste (id lista → id prodotto).
 * Una lista inclusa non risolvibile non contribuisce valori (fail-closed:
 * nessun prodotto la attraversa); una lista esclusa non risolvibile rende il
 * binario CONTA chiuso (`admitsNothing`), mai "entra perché non provato".
 */
export function resolveCommissioningInstancePerimeter(
  perimeter: CommissioningInstancePerimeter | null | undefined,
  resolveList: (listId: string) => Iterable<string> | undefined,
): CommissioningResolvedInstancePerimeter | null {
  const declared = commissioningPerimeterDeclaredDimensions(perimeter);
  if (!perimeter || declared.length === 0) return null;
  const requiredFactKeys = [...new Set(declared.map((dim) => COMMISSIONING_PERIMETER_DIMENSION_FACT_KEY[dim]))];
  const expand = (listIds: readonly string[] | null): { productIds: readonly string[] | null; unresolved: boolean } => {
    if (listIds === null) return { productIds: null, unresolved: false };
    const union = new Set<string>();
    let unresolved = false;
    for (const listId of listIds) {
      const values = resolveList(listId);
      if (values === undefined) { unresolved = true; continue; }
      for (const value of values) union.add(value);
    }
    return { productIds: [...union].sort(), unresolved };
  };
  const included = expand(countIds(perimeter, 'included_list_ids'));
  const excluded = expand(countIds(perimeter, 'excluded_list_ids'));
  return {
    requiredFactKeys,
    count: {
      includedBrandIds: countIds(perimeter, 'included_brand_ids'),
      excludedBrandIds: countIds(perimeter, 'excluded_brand_ids'),
      includedProductIds: included.productIds,
      excludedProductIds: excluded.productIds,
      priceListIds: countIds(perimeter, 'price_list_ids'),
      categoryIds: countIds(perimeter, 'category_ids'),
      productTypeIds: countIds(perimeter, 'product_type_ids'),
      admitsNothing: excluded.unresolved,
    },
  };
}

/**
 * Perimetro risolto dalla FOTO pubblicata del configuratore: le liste modelli
 * si espandono SOLO con i valori congelati in `referenceLists` (il motore
 * legge la foto, mai le liste vive); una lista assente dalla foto è non
 * risolvibile (fail-closed come sopra).
 */
export function resolveCommissioningSnapshotPerimeter(snapshot: {
  instance: { perimeter?: CommissioningInstancePerimeter | null };
  referenceLists?: ReadonlyArray<{ id: string; values: readonly string[] }>;
}): CommissioningResolvedInstancePerimeter | null {
  const perimeter = snapshot.instance.perimeter ?? null;
  if (!perimeter) return null;
  const frozenLists = new Map(snapshot.referenceLists?.map((row) => [row.id, row.values]));
  return resolveCommissioningInstancePerimeter(perimeter, (listId) => frozenLists.get(listId));
}

/** Id delle liste modelli dichiarate nel perimetro (origini da congelare nella foto). */
export function commissioningPerimeterListIds(
  perimeter: CommissioningInstancePerimeter | null | undefined,
): string[] {
  return [...new Set([...(perimeter?.included_list_ids?.ids ?? []), ...(perimeter?.excluded_list_ids?.ids ?? [])])];
}

// ---------------------------------------------------------------------------
// Opzioni perimetro (endpoint unico incentivazione) e scheda
// ---------------------------------------------------------------------------

/** Opzione di una lista viva del tenant (solo riferimenti attivi/non archiviati). */
export const commissioningPerimeterOptionSchema = z.object({
  id: uuid,
  name: z.string(),
}).strict();
export type CommissioningPerimeterOption = z.infer<typeof commissioningPerimeterOptionSchema>;

export const commissioningPerimeterOptionsSchema = z.object({
  brands: z.array(commissioningPerimeterOptionSchema),
  lists: z.array(commissioningPerimeterOptionSchema),
  price_lists: z.array(commissioningPerimeterOptionSchema),
  categories: z.array(commissioningPerimeterOptionSchema),
  product_types: z.array(commissioningPerimeterOptionSchema),
}).strict();
/** DTO di `GET /incentivazione/perimeter-options`. */
export type CommissioningPerimeterOptionsDto = z.infer<typeof commissioningPerimeterOptionsSchema>;

/** Riferimento risolto in scheda: nome del tenant o segnalazione di riferimento non valido. */
export const commissioningPerimeterResolvedRefSchema = z.object({
  id: uuid,
  name: z.string().nullable(),
  /** `active` = valido; `archived` = archiviato/disattivo; `missing` = non più esistente nel tenant. */
  status: z.enum(['active', 'archived', 'missing']),
}).strict();
export type CommissioningPerimeterResolvedRef = z.infer<typeof commissioningPerimeterResolvedRefSchema>;

export const commissioningPerimeterResolvedDimensionSchema = z.object({
  dimension: z.enum(COMMISSIONING_PERIMETER_DIMENSIONS),
  applies_to: z.enum(COMMISSIONING_PERIMETER_APPLIES_TO),
  refs: z.array(commissioningPerimeterResolvedRefSchema),
}).strict();
export type CommissioningPerimeterResolvedDimension = z.infer<typeof commissioningPerimeterResolvedDimensionSchema>;

/** Sezione Perimetro della scheda configuratore: dimensioni dichiarate con nomi risolti. */
export const commissioningPerimeterSchedaSchema = z.object({
  declared: z.boolean(),
  dimensions: z.array(commissioningPerimeterResolvedDimensionSchema),
}).strict();
export type CommissioningPerimeterScheda = z.infer<typeof commissioningPerimeterSchedaSchema>;
